import React, { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
    Button,
    createStyles,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    InputLabel,
    makeStyles,
    MenuItem,
    Select,
    TextField,
    Theme,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { UserListDto } from '../../../api/user';
import { DictionaryDto } from '../../../api/dictionary';
import { fetchStaffActiveCouriers } from '../../../api/couriers';
import { fetchRegionsDict } from '../../../api/regions';
import Spin from '../../atoms/Spin/Spin';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        formControl: {
            minWidth: 120,
            width: '100%',
            marginTop: theme.spacing(2),
        },
    })
);

interface Props {
    open: boolean;
    onSelect: (courierId: number | undefined) => void;
    onClose: () => void;
    loading: boolean;
    parentRegionId: number | string;
    parentProduct: string | null;
    parentBranchCode: string | null;
}

export const OrderCourierDialog = ({
    open,
    onSelect,
    loading,
    onClose,
    parentRegionId,
    parentProduct,
    parentBranchCode
}: Props) => {
    const { enqueueSnackbar } = useSnackbar();
    const classes = useStyles();
    const [activeCouriers, setActiveCouriers] = useState<UserListDto[]>([]);
    const [courier, setCourier] = useState<UserListDto | null>(null);
    const [regionId, setRegionId] = useState<number | string>('');
    const [regions, setRegions] = useState<DictionaryDto[]>([]);

    const handleSelect = useCallback(() => {
        onSelect(courier?.id);
    }, [courier, onSelect]);

    const loadActiveCouriers = useCallback(
        async (value: number | string) => {
            if (value) {
                const regionCode = regions.find((r) => r.id === value)?.code;
                const courierList = await fetchStaffActiveCouriers(regionCode || null, parentProduct || null, parentBranchCode || null);
                setActiveCouriers(courierList.content);
            } else {
                setActiveCouriers([]);
            }

            setCourier(null);
        },
        [regions, parentProduct, parentBranchCode]
    );

    const handleRegionChange = useCallback(
        async (e: any) => {
            const value = e.target.value as string;
            setRegionId(value);
            await loadActiveCouriers(value);
        },
        [loadActiveCouriers]
    );

    const getDictionaries = useCallback(async () => {
        try {
            setRegions(await fetchRegionsDict());
        } catch (e) {
            enqueueSnackbar('Ошибка при загрузке справочников', { variant: 'error' });
        }
    }, [enqueueSnackbar]);

    useEffect(() => {
        if (open) {
            setRegionId(parentRegionId);
            // setProductCode(parentProduct);
            // setBranchCode(parentBranchCode)
            loadActiveCouriers(parentRegionId);
        }
    }, [loadActiveCouriers, open, parentRegionId, parentProduct, parentBranchCode]);

    useEffect(() => {
        getDictionaries();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Dialog
            open={open}
            fullWidth={true}
            onClose={() => {
                onClose();
            }}
        >
            <DialogTitle>Выберите курьера</DialogTitle>
            <DialogContent>
                {loading && <Spin />}
                <FormControl className={classes.formControl} variant="outlined" error={!regionId}>
                    <InputLabel id="product-id-label">Регион</InputLabel>
                    <Select
                        labelId="product-id-label"
                        id="product-id"
                        value={regionId}
                        label="Регион"
                        onChange={handleRegionChange}
                    >
                        {regions.length > 1 && (
                            <MenuItem value="">
                                <em>Не выбрано</em>
                            </MenuItem>
                        )}
                        {regions.map((region, index) => (
                            <MenuItem key={index} value={region.id}>
                                {region.description}
                            </MenuItem>
                        ))}
                    </Select>
                    {!regionId && <FormHelperText>Выберите регион</FormHelperText>}
                </FormControl>
                <FormControl className={classes.formControl} error={!courier?.id}>
                    <Autocomplete
                        id="courier"
                        options={activeCouriers}
                        disabled={regionId === ''}
                        getOptionLabel={(option: UserListDto) =>
                            `${option.last_name} ${option.first_name} (${option.phone_number})`
                        }
                        //@ts-ignore
                        onChange={(event: any, newValue: UserListDto | null) => {
                            setCourier(newValue);
                        }}
                        value={courier}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                error={!courier?.id}
                                label="Курьер"
                                variant="outlined"
                            />
                        )}
                    />
                    {!courier?.id && <FormHelperText>Выберите курьера</FormHelperText>}
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Отмена</Button>
                <Button disabled={!(regionId && courier?.id)} onClick={handleSelect}>
                    Ок
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default OrderCourierDialog;
